import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
  Box,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Select from "react-select";
import moment from "moment";
import Axios from "axios";
import PdfReportOutstandingDues from "./PdfReportOutstandingDues";
import Sidebar from "../../sidebar/sidebar";
import { BaseUrl } from "../../../Environment";
import { companylogo } from "../../../assets/images";
import { getSecureItem } from "../../../utilsFunctions";
import { withNamespaces } from "react-i18next";
import { getCompanyProfile } from "../../../api";
import excel from "../../../assets/images/excel.svg";
import pdf from "../../../assets/images/pdf.svg";
import { Workbook } from "react-excel-workbook";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// ── Constants ────────────────────────────────────────────────────────────────

const SEQUENCE_FILTER = [
  { value: "onetime", label: "Onetime" },
  { value: "daily", label: "Daily" },
  ...(getSecureItem("loggedinUserCompany") == 2891
    ? [{ value: "ten_daily", label: "Ten_Daily" }]
    : []),
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: null, label: "All" },
];

const TABLE_HEADERS = [
  { key: "srNo", label: "Sr No" },
  { key: "loanId", label: "Loan Id" },
  { key: "customerName", label: "Name" },
  { key: "areaLine", label: "Area/Line" },
  { key: "mobileNumber", label: "Phone" },
  { key: "collectionAgent", label: "Collection Agent" },
  { key: "dueTillDate", label: "Due Till Date" },
  { key: "amountPaidTillDate", label: "Amt Collected Till-Date" },
  { key: "pendingDue", label: "Pending Due" },
  { key: "lastPaymentDate", label: "Last Payment Date" },
  { key: "outstandingBalance", label: "Outstanding Balance" },
];

// ── Styled components ─────────────────────────────────────────────────────────

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: "center",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

// ── Helpers ───────────────────────────────────────────────────────────────────

const sum = (arr, key) =>
  arr.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);

const formatCurrency = (symbol, value) =>
  `${symbol} ${Number(value || 0).toLocaleString()}`;

// ── Component ─────────────────────────────────────────────────────────────────

const OutstandingDuesReport = () => {
  const history = useHistory();
  const loggedinUser = getSecureItem("loggedinUser");
  const companyId = getSecureItem("loggedinUserCompany");
  const token = getSecureItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const currencySymbol = getSecureItem("country")?.symbol || "";

  // ── State ──────────────────────────────────────────────────────────────────
  const [branchData, setBranchData] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [companyData, setCompanyData] = useState({});
  const [reportTableData, setReportTableData] = useState([]);
  const [loanType, setLoanType] = useState(null);
  const [loanTypeData, setLoanTypeData] = useState([]);
  const [loanTenure, setLoanTenure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cAgentId, setCAgentId] = useState(null);
  const [cAgentData, setCAgentData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [boxData, setBoxData] = useState([]);
  const [areaLine, setAreaLine] = useState(null);
  const [dataAL, setDataAL] = useState([]);

  const userType = loggedinUser?.userType;

  // ── Data fetchers ──────────────────────────────────────────────────────────

  const getLoanTypes = async () => {
    try {
      const { data } = await Axios.get(
        `${BaseUrl}/loanType/getAllLoanTypes?loanType=&companyId=${companyId}`,
        { headers },
      );
      setLoanTypeData(
        data?.map((d) => ({ value: d.loanTypeId, label: d.loanType })) || [],
      );
    } catch (err) {
      console.error("Loan type error:", err);
    }
  };

  const getAllUsers = async () => {
    try {
      const branchId =
        userType === "superAdmin"
          ? selectedBranch?.value || ""
          : selectedBranch?.value || loggedinUser?.branchId || "";

      const { data } = await Axios.get(
        `${BaseUrl}/user/getAllUsers?userName=&companyId=${companyId}&branchId=${branchId}`,
        { headers },
      );
      const users = data?.result || [];
      const filtered =
        userType === "Collection Agent"
          ? users.filter((u) => u.userId === loggedinUser?.userId)
          : users;

      setCAgentData(
        filtered.map((u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u.userId,
        })),
      );
    } catch (err) {
      console.error("Users fetch error:", err);
    }
  };

  const getCompany = async () => {
    try {
      const {
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        AddressStreet,
        AddressLandmark,
        AddressCity,
        AddressState,
        AddressPincode,
      } = await getCompanyProfile();

      setCompanyData({
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        address: {
          AddressStreet,
          AddressLandmark,
          AddressCity,
          AddressState,
          AddressPincode,
        },
      });
    } catch (err) {
      console.error("Company fetch error:", err);
    }
  };

  const fetchBranches = async () => {
    try {
      const { data } = await Axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId: loggedinUser.companyId },
        headers,
      });
      const branches =
        userType === "superAdmin"
          ? data?.data
          : data?.data?.filter((b) => b?.branchId === loggedinUser?.branchId);

      setBranchData(
        branches?.map((b) => ({ label: b.branchName, value: b.branchId })) ||
          [],
      );
    } catch (err) {
      console.error("Branches fetch error:", err);
    }
  };

  // ── Report API ─────────────────────────────────────────────────────────────

  const getOutstandingReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        companyId,
        outstandingDues: true,
        searchQuery,
        loanTypeId: loanType?.value || null,
        loanTenureType: loanTenure || null,
        collectorId: cAgentId?.value || null,
        areaLine: areaLine,
        branchId:
          userType === "superAdmin"
            ? selectedBranch?.value || null
            : selectedBranch?.value || loggedinUser?.branchId || null,
      };

      const { data } = await Axios.post(
        `${BaseUrl}/loanCollection/getOutstandingLoansReport`,
        payload,
        { headers },
      );
      setReportTableData(data?.result || []);
    } catch (err) {
      console.error("Outstanding report error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, searchQuery, companyId, loanType, loanTenure, cAgentId, areaLine]);

  const getAreaLines = useCallback(async () => {
    try {
      const { data } = await Axios.get(
        `${BaseUrl}/customer/getAreaLines?companyId=${companyId}`,
        { headers },
      );

      setDataAL(data?.data || []);
    } catch (err) {
      console.error("Outstanding report error:", err);
    } 
  }, [companyId]);

  // ── Summary cards ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!reportTableData?.length) {
      setBoxData([]);
      return;
    }

    setBoxData([
      {
        label: "Total Due Till Date",
        value: sum(reportTableData, "totalDueTillDate"),
      },
      {
        label: "Total Collected",
        value: sum(reportTableData, "totalCollectedTillDate"),
      },
      { label: "Total Pending Due", value: sum(reportTableData, "pendingDue") },
      {
        label: "Total Outstanding Balance",
        value: sum(reportTableData, "outstandingBalance"),
      },
    ]);
  }, [reportTableData]);

  // ── Init ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchBranches();
    getLoanTypes();
    getAllUsers();
    getCompany();
  }, []);

  useEffect(() => {
    getOutstandingReport();
  }, [getOutstandingReport]);

  useEffect(() => {
    getAreaLines();
  }, [getAreaLines]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const resetData = () => {
    setCAgentId(null);
    setLoanType(null);
    setLoanTenure(null);
    setSelectedBranch(null);
    setAreaLine(null);
    setSearchQuery("");
  };

  const getLabelByValue = (value) =>
    cAgentData?.find((item) => item.value === value)?.label || "-";

  const navigateToLoanDetails = (loanId) =>
    history.push({
      pathname: "/customers/customerinfo",
      state: { loanID: loanId },
    });

  const handlePdfDownload = () =>
    PdfReportOutstandingDues({
      sortedData: reportTableData,
      selectedCA: cAgentId?.value || null,
      loanType: loanType?.value || null,
      loanTypeData,
      loanTenure,
      companyData,
      cAgentData,
      boxData,
    });

  // ── Excel row mapper ───────────────────────────────────────────────────────

  const excelRows = reportTableData.map((row) => ({
    loanId: row.loanId,
    custId: row?.customer?.cust_id || "",
    customerName: `${row?.customer?.firstName || ""} ${row?.customer?.lastName || ""}`,
    areaLine: row?.customer?.areaLine || "",
    phone: row?.customer?.phone || "",
    collectionAgent: getLabelByValue(row?.collectorId),
    totalDueTillDate: row?.totalDueTillDate || 0,
    totalCollectedTillDate: row?.totalCollectedTillDate || 0,
    pendingDue: row?.pendingDue || 0,
    lastPaymentDate: row?.lastPaymentDate
      ? moment(row.lastPaymentDate).format("LL")
      : "-",
    outstandingBalance: row?.outstandingBalance || 0,
  }));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: 73, height: "100vh" }}>
        {loading ? (
          <Grid container justifyContent="center" mt={2}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container direction="column">
            {/* Logo */}
            <Grid item sx={{ backgroundColor: "#fff" }}>
              <Box m={0.5}>
                <img src={companylogo} width={200} height={40} alt="logo" />
              </Box>
            </Grid>

            {/* Title */}
            <strong
              style={{ textAlign: "center", marginTop: 10, fontSize: 18 }}
              className="Fonts"
            >
              Outstanding Dues Report
            </strong>

            {/* Filters */}
            <Grid container spacing={2} px={4} py={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Select
                  options={cAgentData}
                  value={cAgentId}
                  onChange={setCAgentId}
                  placeholder="Select Collection Agent"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Select
                  options={loanTypeData}
                  value={loanType}
                  onChange={setLoanType}
                  placeholder="Select Loan Type"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Select
                  options={branchData}
                  isDisabled={loggedinUser?.company?.planId != 3}
                  value={selectedBranch}
                  onChange={setSelectedBranch}
                  placeholder="Select Branch"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Select
                  options={SEQUENCE_FILTER}
                  value={
                    SEQUENCE_FILTER.find((s) => s.value === loanTenure) || null
                  }
                  onChange={(val) => setLoanTenure(val?.value || null)}
                  placeholder="Select Loan Tenure"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Select
                  options={dataAL}
                  value={
                    dataAL.find((al) => al.value === areaLine) || null
                  }
                  onChange={(val) => setAreaLine(val?.value || null)}
                  placeholder="Select Area/Line"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  hiddenLabel
                  value={searchQuery}
                  type="text"
                  placeholder="Search by name, loan id, phone..."
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Stack
                width="100%"
                display="flex"
                direction="row"
                alignItems="center"
                gap={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={resetData}
                  style={{ width: 150 }}
                >
                  Reset
                </Button>
                <p className="Fonts">Download: </p>
                <img
                  src={pdf}
                  alt="pdf"
                  className="pdficon"
                  style={{ cursor: "pointer" }}
                  onClick={handlePdfDownload}
                />
                <Workbook
                  filename="outstanding_dues_report.xlsx"
                  element={
                    <img
                      src={excel}
                      alt="excel"
                      className="excelicon"
                      style={{ cursor: "pointer" }}
                    />
                  }
                >
                  <Workbook.Sheet
                    data={excelRows}
                    name="Outstanding Dues Report"
                  >
                    <Workbook.Column label="Loan Id" value="loanId" />
                    <Workbook.Column label="Customer Id" value="custId" />
                    <Workbook.Column
                      label="Customer Name"
                      value="customerName"
                    />
                    <Workbook.Column
                      label="Area/Line"
                      value="areaLine"
                    />
                    <Workbook.Column label="Mobile Number" value="phone" />
                    <Workbook.Column
                      label="Collection Agent"
                      value="collectionAgent"
                    />
                    <Workbook.Column
                      label="Due Till Date"
                      value="totalDueTillDate"
                    />
                    <Workbook.Column
                      label="Amount Paid Till Date"
                      value="totalCollectedTillDate"
                    />
                    <Workbook.Column label="Pending Due" value="pendingDue" />
                    <Workbook.Column
                      label="Last Payment Date"
                      value="lastPaymentDate"
                    />
                    <Workbook.Column
                      label="Outstanding Balance"
                      value="outstandingBalance"
                    />
                  </Workbook.Sheet>
                </Workbook>
              </Stack>
            </Grid>

            {/* Summary Cards */}
            <Grid container spacing={2} px={4} pb={2}>
              {boxData.map((item, i) => (
                <Grid key={i} item xs={12} sm={6} md={3}>
                  <Card
                    elevation={1}
                    sx={{
                      borderRadius: 3,
                      boxShadow: "0px 3px 10px 4px rgba(36,38,52,0.1)",
                    }}
                  >
                    <CardContent>
                      <div
                        className="Fonts"
                        style={{ fontSize: 22, fontWeight: 600 }}
                      >
                        {i === 0
                          ? Number(item.value).toLocaleString()
                          : formatCurrency(currencySymbol, item.value)}
                      </div>
                      <div
                        className="Fonts"
                        style={{ fontSize: 14, color: "#666", marginTop: 4 }}
                      >
                        {item.label}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Table */}
            <Grid item container px={4} py={2}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 900, overflow: "auto" }}>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEADERS.map(({ key, label }) => (
                        <StyledTableCell key={key}>{label}</StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportTableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          align="center"
                          sx={{ py: 4, color: "#999" }}
                        >
                          No outstanding dues found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportTableData.map((row, index) => (
                        <StyledTableRow key={row?.loanId || index}>
                          <StyledTableCell>{index + 1}</StyledTableCell>

                          <StyledTableCell>
                            <span
                              onClick={() => navigateToLoanDetails(row?.loanId)}
                              style={{
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {row?.loanId}
                            </span>
                          </StyledTableCell>

                          <StyledTableCell>
                            <div>{`${row?.customer?.firstName || ""} ${row?.customer?.lastName || ""}`}</div>
                            {row?.customer?.cust_id && (
                              <div>({row.customer.cust_id})</div>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row?.customer?.areaLine}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row?.customer?.phone || "-"}
                          </StyledTableCell>

                          <StyledTableCell>
                            {getLabelByValue(row?.collectorId)}
                          </StyledTableCell>

                          <StyledTableCell>
                            {formatCurrency(
                              currencySymbol,
                              row?.totalDueTillDate,
                            )}
                          </StyledTableCell>

                          <StyledTableCell>
                            {formatCurrency(
                              currencySymbol,
                              row?.totalCollectedTillDate,
                            )}
                          </StyledTableCell>

                          <StyledTableCell
                            sx={{
                              color:
                                Number(row?.pendingDue) > 0
                                  ? "#d32f2f"
                                  : "inherit",
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(currencySymbol, row?.pendingDue)}
                          </StyledTableCell>

                          <StyledTableCell>
                            {row?.lastPaymentDate
                              ? moment(row.lastPaymentDate).format("LL")
                              : "-"}
                          </StyledTableCell>

                          <StyledTableCell sx={{ fontWeight: 600 }}>
                            {formatCurrency(
                              currencySymbol,
                              row?.outstandingBalance,
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </div>
    </>
  );
};

export default withNamespaces()(OutstandingDuesReport);
